// Builds the post-booking thank-you redirect URL and reports exactly what was
// carried over. Every field is optional: whatever attribution exists at
// booking time is appended, anything missing is simply skipped so the URL can
// never end up malformed (no empty `?`, no `utm_source=null`).

import { getAttribution } from "./attribution";

export const THANK_YOU_URL = "https://www.inflow-agency.com/thank-you-page-introcall";

export const CARRIED_PARAM_KEYS = [
  "email",
  "phone",
  "first_name",
  "last_name",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_campaign_id",
  "utm_adset_id",
  "utm_ad_id",
  "gclid",
  "fbclid",
] as const;

export type CarriedParamKey = (typeof CARRIED_PARAM_KEYS)[number];

export type BookingContact = {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

export type RedirectPlan = {
  url: string;
  params: Partial<Record<CarriedParamKey, string>>;
  /** Keys we wanted but had no value for. Never blocks the redirect. */
  missing: CarriedParamKey[];
};

function clean(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  // Guard against literal "null"/"undefined" strings sneaking in from storage.
  if (s === "null" || s === "undefined") return null;
  return s;
}

/**
 * Assembles the thank-you redirect. Safe to call with zero attribution:
 * the base URL is returned untouched when nothing is available.
 */
export function buildThankYouRedirect(contact: BookingContact = {}): RedirectPlan {
  let last: Record<string, string | null> = {};
  try {
    last = (getAttribution().last ?? {}) as unknown as Record<string, string | null>;
  } catch {
    last = {};
  }

  const candidates: Record<CarriedParamKey, string | null> = {
    email: clean(contact.email),
    phone: clean(contact.phone),
    first_name: clean(contact.firstName),
    last_name: clean(contact.lastName),
    utm_source: clean(last.utm_source),
    utm_medium: clean(last.utm_medium),
    utm_campaign: clean(last.utm_campaign),
    utm_content: clean(last.utm_content),
    utm_term: clean(last.utm_term),
    utm_campaign_id: clean(last.utm_campaign_id),
    utm_adset_id: clean(last.utm_adset_id),
    utm_ad_id: clean(last.utm_ad_id),
    gclid: clean(last.gclid),
    fbclid: clean(last.fbclid),
  };

  const search = new URLSearchParams();
  const params: Partial<Record<CarriedParamKey, string>> = {};
  const missing: CarriedParamKey[] = [];

  for (const key of CARRIED_PARAM_KEYS) {
    const value = candidates[key];
    if (value) {
      search.set(key, value);
      params[key] = value;
    } else {
      missing.push(key);
    }
  }

  const qs = search.toString();
  return { url: qs ? `${THANK_YOU_URL}?${qs}` : THANK_YOU_URL, params, missing };
}
